import { useEffect, useState } from 'react';
import type { FellowConnectionResponse, LogoutResponse } from '../../shared/api-types.js';
import { displayVersion } from '../../shared/text.js';
import { api, type ApiFailure } from '../api.js';
import { Avatar } from '../ui/Avatar.js';
import { Icon } from '../ui/Icon.js';
import { fellowAccountLine, type FellowAccountLine } from '../pages/lib/connection.js';
import { MenuItem, MenuPopover, MenuSeparator, useMenu } from '../ui/Menu.js';
import { failureDetail } from '../ui/StateView.js';
import { useToast } from '../ui/Toast.js';
import { adminHome, useMe } from './me-context.js';

/**
 * The account menu (03-components §15). Trigger: the user's initials, a real button with
 * aria-haspopup="menu", an accessible name carrying the user's name, at the trailing edge of the
 * bar at every width. Panel regions in fixed order: identity · app context · own destinations ·
 * privileged destinations · sign out · version. Regions 1 and 6 sit outside the role="menu"
 * container: facts, not items.
 */
export function AccountMenu() {
  const me = useMe();
  const menu = useMenu();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  const administration = adminHome(me);

  const signOut = async (): Promise<void> => {
    if (signingOut) return;
    setSigningOut(true);
    const result = await api.post<LogoutResponse>('/auth/logout');
    if (result.ok) {
      window.location.assign('/signed-out?reason=signed_out');
      return;
    }
    setSigningOut(false);
    toast.error(<>We couldn&rsquo;t sign you out. {failureDetail(result)}</>);
  };

  return (
    <>
      <button
        {...menu.triggerProps}
        type="button"
        className="account-trigger hit-area"
        aria-label={`${me.subject.displayName} — account menu`}
        title={me.subject.displayName}
      >
        <Avatar name={me.subject.displayName} email={me.subject.email} hashKey={me.subject.id} initials={me.subject.initials} size="md" decorative />
      </button>
      <MenuPopover menu={menu} label="Account" align="end" minWidth="17rem" className="account-panel">
        <div className="account-panel__identity">
          <Avatar name={me.subject.displayName} email={me.subject.email} hashKey={me.subject.id} initials={me.subject.initials} size="lg" decorative />
          <div className="account-panel__who">
            <p className="account-panel__name">{me.subject.displayName}</p>
            <p className="account-panel__email">{me.subject.email}</p>
          </div>
        </div>
        <FellowContextLine open={menu.open} mode={me.integrations.fellow} />
        <div role="menu" aria-label="Account" className="menu">
          <MenuItem to="/settings" icon="settings">Settings</MenuItem>
          {administration ? <MenuItem to={administration} icon="shield">Administration</MenuItem> : null}
          <MenuSeparator />
          <MenuItem icon="sign-out" onSelect={() => void signOut()} disabled={signingOut} disabledReason={signingOut ? 'Signing out…' : undefined}>Sign out</MenuItem>
        </div>
        <p className="account-panel__version">
          <span className="muted">Calis </span>
          <span className="selectable">{displayVersion(me.version)}</span>
          {me.environment !== 'production' ? <span className="muted"> · {me.environment}</span> : null}
        </p>
      </MenuPopover>
    </>
  );
}

// Why the words live in pages/lib/connection.ts (`fellowAccountLine`): a key Fellow no longer accepts reads "Revoked —
// reconnect" here, in the Fellow panel's banner and on its Credential row alike (05-content §6; Phase 1c R12).
function describeConnection(response: FellowConnectionResponse | null, failure: ApiFailure | null, loading: boolean): FellowAccountLine {
  return fellowAccountLine(response, failure !== null, loading);
}

/** Region 2 — app context: the Fellow connection state, fetched when the panel opens. */
function FellowContextLine({ open, mode }: { open: boolean; mode: 'live' | 'fixture' | 'off' }) {
  const [state, setState] = useState<{ loading: boolean; response: FellowConnectionResponse | null; failure: ApiFailure | null }>({ loading: false, response: null, failure: null });

  useEffect(() => {
    if (!open || mode === 'off') return;
    const controller = new AbortController();
    setState({ loading: true, response: null, failure: null });
    void api.get<FellowConnectionResponse>('/api/connections/fellow', { signal: controller.signal }).then((result) => {
      if (controller.signal.aborted) return;
      if (result.ok) setState({ loading: false, response: result.data, failure: null });
      else if (!result.aborted) setState({ loading: false, response: null, failure: result });
    });
    return () => controller.abort();
  }, [open, mode]);

  const line = mode === 'off'
    ? describeConnection({ connection: null, runs: { last: null, unattendedStreak: 0 }, allowed: false, floor: 'not_configured', mode, tickOff: { floor: 'off', allowed: false, failing: 0, dailyLimitReached: false }, fellowState: { floor: 'off', allowed: false, error: null, dailyOn: null }, syncEveryMinutes: 15 }, null, false)
    : describeConnection(state.response, state.failure, state.loading);
  return (
    <p className={`account-panel__context account-panel__context--${line.tone}`}>
      <Icon name={line.icon} size={14} />
      <span>{line.text}</span>
    </p>
  );
}
